import React, { useState, useEffect } from 'react';
import {
  Card, CardBody, Col, Container, Row,
  Form, FormGroup, Label, Input, Button, Spinner
} from 'reactstrap';
import MetaTags from "react-meta-tags";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useHistory } from "react-router-dom";
import ApiClient from '../BaseUrl/ApiClient';

function ProfileSetup() {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({
    password: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    const authUser = JSON.parse(localStorage.getItem('authUser'));
    const token = localStorage.getItem('accessToken');
    
    if (!token) {
      history.push('/login');
      return;
    }

    if (authUser) {
      setFormData(prev => ({
        ...prev,
        name: authUser.name || '',
        email: authUser.email || ''
      }));
    }
  }, [history]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validatePasswordFields = () => {
    // Only validate password fields if any password field has value
    if (!formData.password && !formData.newPassword && !formData.confirmPassword) {
      return true;
    }

    let valid = true;
    const newErrors = {
      password: '',
      newPassword: '',
      confirmPassword: ''
    };

    if (!formData.password) {
      newErrors.password = 'Current password is required';
      valid = false;
    }

    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
      valid = false;
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
      valid = false;
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
      valid = false;
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

 const handleSubmit = async (e) => {
  e.preventDefault();
  const passwordFieldsNotEmpty =
    formData.password || formData.newPassword || formData.confirmPassword;

  if (passwordFieldsNotEmpty && !validatePasswordFields()) {
    return;
  }

  setLoading(true);

  const token = localStorage.getItem("accessToken");

  if (!token) {
    toast.error("Please login to continue");
    history.push("/login");
    setLoading(false);
    return;
  }

  try {
    const profileResponse = await ApiClient().put("/auth/business/update", {
      name: formData.name,
      email: formData.email,
    });

    if (passwordFieldsNotEmpty) {
      await ApiClient().put(
        "/auth/business/changePassword",
        {
          password: formData.password,
          newPassword: formData.newPassword,
          confirmPassword: formData.confirmPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    }

    toast.success("Profile updated successfully!");

    const authUser = JSON.parse(localStorage.getItem("authUser"));
    localStorage.setItem(
      "authUser",
      JSON.stringify({
        ...authUser,
        name: formData.name,
        email: formData.email,
      })
    );

    if (passwordFieldsNotEmpty) {
      setFormData((prev) => ({
        ...prev,
        password: "",
        newPassword: "",
        confirmPassword: "",
      }));
    }
  } catch (error) {
    if (error.response && error.response.status === 401) {
      toast.error("Session expired. Please login again.");
    } else {
      const errorMessages = error.response?.data?.responseMessage;

      if (Array.isArray(errorMessages)) {
        errorMessages.forEach((msg) => toast.error(msg)); // show each error
      } else if (typeof errorMessages === "string") {
        toast.error(errorMessages);
      } else {
        toast.error(
          "An error occurred while updating your profile. Please try again later."
        );
      }

      console.error(
        "Error updating profile:",
        error.response?.data?.responseMessage
      );
    }
  } finally {
    setLoading(false);
  }
};


  return (
    <React.Fragment>
      <ToastContainer />
      <div className="page-content">
        <Container fluid className="mt-5">
          <MetaTags>
            <title>Profile Setup</title>
          </MetaTags>
          <Row className="align-items-center topbp">
            <Col xl={6} className="d-flex align-items-center">
              <h3 className="bp-title mb-0">Profile Setup</h3>
            </Col>
            <Col xl={6} className="d-flex justify-content-end align-items-center">
              {/* Optional back button can be added here */}
            </Col>
          </Row>
          <div className="bp-separator"></div>
        
          <Row className="justify-content-center">
            <Col md={8} lg={6}>
              <Card className="shadow">
                <CardBody>
                  <Form onSubmit={handleSubmit} className="p-4">
                    <FormGroup>
                      <Label for="name">Business User Name</Label>
                      <Input
                        type="text"
                        name="name"
                        id="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="mb-3"
                      />
                    </FormGroup>
                    
                    <FormGroup>
                      <Label for="email">Email</Label>
                      <Input
                        type="email"
                        name="email"
                        id="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="mb-4"
                      />
                    </FormGroup>
                    
                    
                    <FormGroup className="position-relative mb-4">
  <Label for="password">Current Password</Label>
  <div className="position-relative"> 
    <Input
      type={showPassword ? "text" : "password"}
      name="password"
      id="password"
      value={formData.password}
      onChange={handleInputChange}
      className="mb-1"
    />
    <i 
      className={showPassword ? "ri-eye-off-line" : "ri-eye-line"} 
      style={{ 
        position: 'absolute',
        right: '10px', 
        top: '50%', 
        transform: 'translateY(-50%)',
        cursor: 'pointer'
      }}
      onClick={() => setShowPassword(!showPassword)}
    ></i>
  </div>
  {errors.password && <small className="text-danger">{errors.password}</small>}
</FormGroup>

<FormGroup className="position-relative mb-4">
  <Label for="newPassword">New Password</Label>
  <div className="position-relative"> 
    <Input
      type={showNewPassword ? "text" : "password"}
      name="newPassword"
      id="newPassword"
      value={formData.newPassword}
      onChange={handleInputChange}
      className="mb-1"
    />
    <i 
      className={showNewPassword ? "ri-eye-off-line" : "ri-eye-line"} 
      style={{ 
        position: 'absolute',
        right: '10px', 
        top: '50%', 
        transform: 'translateY(-50%)',
        cursor: 'pointer'
      }}
      onClick={() => setShowNewPassword(!showNewPassword)}
    ></i>
  </div>
  {errors.newPassword && <small className="text-danger">{errors.newPassword}</small>}
</FormGroup>

<FormGroup className="position-relative mb-4"> {/* Added mb-4 for consistent spacing */}
  <Label for="confirmPassword">Confirm New Password</Label>
  <div className="position-relative"> {/* Wrapper div for input and icon */}
    <Input
      type={showConfirmPassword ? "text" : "password"}
      name="confirmPassword"
      id="confirmPassword"
      value={formData.confirmPassword}
      onChange={handleInputChange}
      className="mb-1"
    />
    <i 
      className={showConfirmPassword ? "ri-eye-off-line" : "ri-eye-line"} 
      style={{ 
        position: 'absolute',
        right: '10px', 
        top: '50%', 
        transform: 'translateY(-50%)',
        cursor: 'pointer'
      }}
      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
    ></i>
  </div>
  {errors.confirmPassword && <small className="text-danger">{errors.confirmPassword}</small>}
</FormGroup>
                    
                    <div className="text-end mt-4">
                      <Button type="submit" color="primary" disabled={loading} className="px-4 py-2">
                        {loading ? (
                          <>
                            <Spinner size="sm" /> Updating...
                          </>
                        ) : (
                          'Update Profile'
                        )}
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
}

export default ProfileSetup;